import { Fragment, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { GlobalHeader } from '@/components/GlobalHeader';
import { useData } from '@/hooks/useData';
import { apiFetch } from '@/services/apiGateway';
import { clearTimetableRequired } from '@/utils/academicReset';

const DAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

const GRID_DAYS: { label: string; key: string }[] = [
  { label: 'Mon', key: 'monday' },
  { label: 'Tue', key: 'tuesday' },
  { label: 'Wed', key: 'wednesday' },
  { label: 'Thu', key: 'thursday' },
  { label: 'Fri', key: 'friday' },
];

const GRID_HOURS = [
  '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00',
];

const timeLabelStyle: CSSProperties = {
  color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600,
  textAlign: 'center', paddingTop: '6px',
};

const dayHeaderStyle: CSSProperties = {
  color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600, textAlign: 'center',
};

interface TimetableEntry {
  subject: string;
  day: string;
  start_time: string;
  end_time: string;
  room: string | null;
}

interface TimetableResponse {
  entries: TimetableEntry[];
}

interface EntryForm {
  subject: string;
  day: string;
  start_time: string;
  end_time: string;
  room: string;
}

const emptyForm = (): EntryForm => ({
  subject: '',
  day: 'monday',
  start_time: '08:00',
  end_time: '09:00',
  room: '',
});

export function ManageTimetablePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { childId: ownChildId } = useAuthStore();
  // Present when a parent is editing a specific child's timetable
  // (/parent/child/:childId/timetable/edit); absent for the student's own
  // self-service route (/student/timetable/edit).
  const { childId: paramChildId } = useParams<{ childId?: string }>();
  const childId = paramChildId ?? ownChildId;
  const basePath = paramChildId ? `/parent/child/${paramChildId}/timetable` : '/student/timetable';

  const { data, isLoading } = useData<TimetableResponse>(
    childId ? `/children/${childId}/timetable` : null,
  );

  const [localEntries, setLocalEntries] = useState<TimetableEntry[]>([]);
  const [form, setForm] = useState<EntryForm>(emptyForm());
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // OCR state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanBanner, setScanBanner] = useState<string | null>(null);
  const [scanBannerIsError, setScanBannerIsError] = useState(false);

  useEffect(() => {
    const scanned = (location.state as { scannedEntries?: TimetableEntry[] } | null)?.scannedEntries;
    const initialData = scanned ?? data?.entries ?? [];
    if (initialData.length > 0) {
      setLocalEntries(initialData);
    }
  }, [data, location.state]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleAdd = () => {
    const trimmed = form.subject.trim();
    if (!trimmed) return;
    setLocalEntries((prev) => [
      ...prev,
      {
        subject: trimmed,
        day: form.day,
        start_time: form.start_time,
        end_time: form.end_time,
        room: form.room.trim() || null,
      },
    ]);
    setForm(emptyForm());
  };

  const handleRemove = (idx: number) => {
    setLocalEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!childId) return;
    setIsSaving(true);
    try {
      await apiFetch(`/children/${childId}/timetable`, {
        method: 'PUT',
        body: JSON.stringify({ entries: localEntries }),
      });
      clearTimetableRequired(childId);
      showToast(t('manage_timetable.saved_toast'));
      setTimeout(() => navigate(basePath), 800);
    } catch {
      showToast(t('manage_free_time.save_failed_toast'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleOcrUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !childId) return;
    e.target.value = '';
    setScanBanner(null);
    setScanBannerIsError(false);

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      const mediaType = dataUrl.split(';')[0]?.split(':')[1] ?? 'image/jpeg';
      if (!base64) {
        setScanBanner(t('manage_timetable.scan_read_error'));
        setScanBannerIsError(true);
        return;
      }
      setIsScanning(true);
      try {
        const res = await apiFetch<{ entries: TimetableEntry[] }>(
          `/children/${childId}/timetable/ocr`,
          {
            method: 'POST',
            body: JSON.stringify({ image_base64: base64, media_type: mediaType }),
          },
        );
        setLocalEntries(res.entries ?? []);
        setScanBanner(t('manage_timetable.scan_success', { count: res.entries?.length ?? 0 }));
        setScanBannerIsError(false);
      } catch {
        setScanBanner(t('manage_timetable.scan_unclear_error'));
        setScanBannerIsError(true);
      } finally {
        setIsScanning(false);
      }
    };
    reader.onerror = () => {
      setScanBanner(t('manage_timetable.scan_read_error'));
      setScanBannerIsError(true);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute={basePath} customTitle={t('manage_timetable.edit_title')} />
        <div className="app-content" style={{ paddingBottom: '80px' }}>

          {/* VADOStudy brand label */}
          <p style={{ margin: '0 0 2px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--primary)' }}>
            VADOStudy
          </p>

          {/* ── Timetable grid preview ── */}
          {localEntries.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <h2 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 10px' }}>
                {t('manage_timetable.preview_heading')}
              </h2>
              <div style={{ width: '100%', overflowX: 'auto', paddingBottom: '8px' }}>
                <div className="timetable-grid">
                  <div />
                  {GRID_DAYS.map((d) => (
                    <div key={d.key} style={dayHeaderStyle}>{d.label}</div>
                  ))}
                  {GRID_HOURS.map((hour) => (
                    <Fragment key={hour}>
                      <div style={timeLabelStyle}>{hour}</div>
                      {GRID_DAYS.map((d) => {
                        const entry = localEntries.find(
                          (e) => e.day === d.key && e.start_time.slice(0, 2) === hour.slice(0, 2),
                        );
                        if (!entry) return <div key={d.key} />;
                        return (
                          <div key={d.key} className="subject-card" title={entry.subject}>
                            {entry.subject}
                            {entry.room && (
                              <span style={{ display: 'block', fontSize: '0.65rem', opacity: 0.7 }}>
                                {entry.room}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Hidden file input for OCR ── */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleOcrUpload}
          />

          {/* ── Loading skeleton ── */}
          {isLoading && (
            <div className="loading-skeleton" style={{ height: '160px', borderRadius: '12px', marginBottom: '24px' }} />
          )}

          {/* ── Option A: Photo scan ── */}
          {!isLoading && (
            <div style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isScanning}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '2px dashed var(--primary)',
                  background: 'var(--primary-light, #EEF2FF)',
                  color: 'var(--primary)',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  cursor: isScanning ? 'wait' : 'pointer',
                  opacity: isScanning ? 0.7 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                {isScanning ? (
                  <>{t('manage_timetable.scanning')}</>
                ) : (
                  <>{t('manage_timetable.scan_photo_btn')}</>
                )}
              </button>

              {scanBanner && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: scanBannerIsError ? '#FEF2F2' : '#F0FDF4',
                  color: scanBannerIsError ? 'var(--danger)' : '#166534',
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                }}>
                  {scanBannerIsError ? '⚠️' : '✅'} {scanBanner}
                </div>
              )}
            </div>
          )}

          {/* ── Divider ── */}
          {!isLoading && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px',
            }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {t('manage_timetable.or_manual')}
              </span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
            </div>
          )}

          {/* ── Option B: Manual form ── */}
          {!isLoading && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 16px' }}>
                {t('manage_timetable.add_class')}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                    {t('manage_timetable.subject_label')}
                  </label>
                  <input
                    type="text"
                    className="input"
                    placeholder={t('student_more.subject_placeholder')}
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                    {t('manage_commitments.day_label')}
                  </label>
                  <select
                    className="input"
                    value={form.day}
                    onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))}
                  >
                    {DAYS.map((d) => (
                      <option key={d} value={d}>
                        {t(`student_timetable.days_full.${d}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                      {t('manage_commitments.start_time_label')}
                    </label>
                    <input
                      type="time"
                      className="input"
                      value={form.start_time}
                      onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                      {t('manage_commitments.end_time_label')}
                    </label>
                    <input
                      type="time"
                      className="input"
                      value={form.end_time}
                      onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                    {t('manage_timetable.room_label')}
                  </label>
                  <input
                    type="text"
                    className="input"
                    placeholder={t('manage_timetable.room_placeholder')}
                    value={form.room}
                    onChange={(e) => setForm((f) => ({ ...f, room: e.target.value }))}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleAdd}
                  disabled={!form.subject.trim()}
                  style={{ width: '100%' }}
                >
                  {t('manage_free_time.add_to_list')}
                </button>
              </div>
            </div>
          )}

          {/* ── Staged entries list ── */}
          {!isLoading && (
            <div>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('manage_timetable.classes_heading', { count: localEntries.length })}
              </h2>

              {localEntries.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center', padding: '24px 0' }}>
                  {t('manage_timetable.empty')}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {localEntries.map((entry, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: 'white', borderRadius: '10px', padding: '12px 14px',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <div>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-dark)' }}>
                          {entry.subject}
                        </p>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          {t(`student_timetable.days_full.${entry.day}`, { defaultValue: entry.day })}
                          {' · '}
                          {entry.start_time}–{entry.end_time}
                          {entry.room ? ` · ${entry.room}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemove(idx)}
                        aria-label={t('manage_timetable.remove_entry_aria')}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '1.1rem', color: 'var(--danger)', padding: '4px',
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* ── Sticky save bar ── */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'white', borderTop: '1px solid var(--border)',
          padding: '12px 16px',
        }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
            style={{ width: '100%' }}
          >
            {isSaving ? t('actions.saving') : t('manage_timetable.save_btn')}
          </button>
        </div>

        {/* ── Toast ── */}
        {toast && (
          <div style={{
            position: 'absolute', bottom: '80px', left: '16px', right: '16px',
            background: 'var(--text-dark)', color: 'white',
            borderRadius: '10px', padding: '12px 16px',
            fontSize: '0.9rem', fontWeight: 500, textAlign: 'center',
            zIndex: 100,
          }}>
            {toast}
          </div>
        )}

      </div>
    </div>
  );
}
